/**
 * DataSourceDialog Component
 *
 * Form dialog for creating/editing datasource configurations
 */

import { useState, useEffect } from "react";
import {
  useAvailableAdapters,
  useDataSourceMutations,
} from "../../hooks/useDataSources";
import { api } from "../../api";
import type { DataSourceDefinition, TenantInfo } from "@onecreator/shared";
import {
  getAvailableOperations,
  pruneUnsupportedOperations,
} from "../../utils/datasourceOperations";
import {
  getWebhookSecret,
  setWebhookSecret,
  validateWebhookSecretInput,
} from "../../utils/datasourceOptions";

type WhoAmITestState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; latency: number; whoami: TenantInfo }
  | { status: "error"; message: string; details?: unknown };

interface DataSourceDialogProps {
  projectId: string;
  isOpen: boolean;
  dataSource?: DataSourceDefinition | null;
  onClose: (saved: boolean) => void;
}

export function DataSourceDialog({
  projectId,
  isOpen,
  dataSource,
  onClose,
}: DataSourceDialogProps) {
  const { adapters, loading: loadingAdapters } = useAvailableAdapters();
  const { createDataSource, updateDataSource, loading, error } =
    useDataSourceMutations(projectId);

  const isEditMode = !!dataSource;

  // Form state
  const [formData, setFormData] = useState<DataSourceDefinition>({
    id: "",
    adapter: "",
    config: {
      baseUrl: "",
      auth: {
        type: "none",
      },
      timeout: 10000,
    },
    enabledOperations: ["suggest", "search", "detail", "list"],
  });

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Test /whoami (W1) — resettato ogni volta che cambia baseUrl o auth
  const [whoamiTest, setWhoamiTest] = useState<WhoAmITestState>({
    status: "idle",
  });
  const isArchivesSemantic = formData.adapter === "archives-semantic";
  const availableOperations = getAvailableOperations(
    adapters,
    formData.adapter,
  );

  async function handleTestWhoAmI() {
    setWhoamiTest({ status: "loading" });
    try {
      const res = await api.post<{
        success: boolean;
        message?: string;
        data?: { latency: number; whoami: TenantInfo };
        details?: unknown;
      }>("/datasource-test/whoami", {
        baseUrl: formData.config.baseUrl,
        auth: formData.config.auth,
      });
      if (res.data.success && res.data.data) {
        setWhoamiTest({
          status: "ok",
          latency: res.data.data.latency,
          whoami: res.data.data.whoami,
        });
      } else {
        setWhoamiTest({
          status: "error",
          message: res.data.message ?? "Errore sconosciuto",
          details: res.data.details,
        });
      }
    } catch (err) {
      const msg =
        (
          err as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ??
        (err as { message?: string })?.message ??
        "Errore di rete";
      setWhoamiTest({ status: "error", message: msg });
    }
  }

  // Reset test se cambia qualcosa che lo rende obsoleto
  useEffect(() => {
    if (whoamiTest.status !== "idle") setWhoamiTest({ status: "idle" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.config.baseUrl,
    formData.config.auth?.credentials,
    formData.config.auth?.type,
    formData.config.auth?.keyName,
    formData.adapter,
  ]);

  // Initialize form data when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (dataSource) {
        setFormData(dataSource);
      } else {
        setFormData({
          id: "",
          adapter: "",
          config: {
            baseUrl: "",
            auth: {
              type: "none",
            },
            timeout: 10000,
          },
          enabledOperations: ["suggest", "search", "detail", "list"],
        });
      }
      setValidationErrors({});
    }
  }, [isOpen, dataSource]);

  // Validate form
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.id.trim()) {
      errors.id = "ID is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.id)) {
      errors.id =
        "ID must contain only lowercase letters, numbers, and hyphens";
    }

    if (!formData.adapter.trim()) {
      errors.adapter = "Adapter is required";
    }

    if (!formData.config.baseUrl.trim()) {
      errors.baseUrl = "Base URL is required";
    } else {
      try {
        new URL(formData.config.baseUrl);
      } catch {
        errors.baseUrl = "Invalid URL format";
      }
    }

    if (formData.config.auth?.type === "apiKey") {
      if (!formData.config.auth.keyName) {
        errors.authKeyName = "Key name is required for API Key auth";
      }
      if (!formData.config.auth.credentials) {
        errors.authCredentials = "Credentials are required for API Key auth";
      }
      if (!formData.config.auth.keyLocation) {
        errors.authKeyLocation = "Key location is required for API Key auth";
      }
    }

    if (
      formData.config.auth?.type === "basic" ||
      formData.config.auth?.type === "bearer"
    ) {
      if (!formData.config.auth.credentials) {
        errors.authCredentials = `Credentials are required for ${formData.config.auth.type} auth`;
      }
    }

    if (isArchivesSemantic) {
      const webhookError = validateWebhookSecretInput(
        getWebhookSecret(formData.config.options),
      );
      if (webhookError) errors.webhookSecret = webhookError;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Il campo esiste solo per archives-semantic: un segreto rimasto da un
    // cambio di adapter non sarebbe più visibile né modificabile → si scarta.
    const payload: DataSourceDefinition = isArchivesSemantic
      ? formData
      : {
          ...formData,
          config: {
            ...formData.config,
            options: setWebhookSecret(formData.config.options, ""),
          },
        };

    try {
      if (isEditMode) {
        await updateDataSource(payload);
      } else {
        await createDataSource(payload);
      }
      onClose(true);
    } catch (err: any) {
      console.error("Save failed:", err);
      // Error is handled by the hook and displayed below
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose(false);
  };

  // Update form field
  const updateField = (path: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const keys = path.split(".");
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Segreto webhook: fonde in config.options (mai le sostituisce), vuoto → rimuove la chiave
  const handleWebhookSecretChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        options: setWebhookSecret(prev.config.options, value),
      },
    }));
  };

  // Cambio adapter: rimuove le operazioni non supportate dal nuovo adapter
  const handleAdapterChange = (adapterId: string) => {
    const available = getAvailableOperations(adapters, adapterId);
    setFormData((prev) => ({
      ...prev,
      adapter: adapterId,
      enabledOperations: pruneUnsupportedOperations(
        prev.enabledOperations || [],
        available,
      ),
    }));
  };

  // Toggle enabled operation
  const toggleOperation = (operation: string) => {
    setFormData((prev) => {
      const ops = prev.enabledOperations || [];
      const newOps = ops.includes(operation as any)
        ? ops.filter((op) => op !== operation)
        : [...ops, operation as any];
      return { ...prev, enabledOperations: newOps };
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleCancel}
        ></div>

        {/* Dialog */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isEditMode ? "Edit Data Source" : "Add Data Source"}
              </h3>

              {/* Error Alert */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => updateField("id", e.target.value)}
                    disabled={isEditMode}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      validationErrors.id ? "border-red-300" : ""
                    } ${isEditMode ? "bg-gray-100" : ""}`}
                    placeholder="e.g., europeana-api"
                  />
                  {validationErrors.id && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.id}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Lowercase letters, numbers, and hyphens only
                  </p>
                </div>

                {/* Adapter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Adapter <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.adapter}
                    onChange={(e) => handleAdapterChange(e.target.value)}
                    disabled={loadingAdapters}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      validationErrors.adapter ? "border-red-300" : ""
                    }`}
                  >
                    <option value="">Select an adapter...</option>
                    {adapters.map((adapter) => (
                      <option key={adapter.id} value={adapter.id}>
                        {adapter.name} - {adapter.description}
                      </option>
                    ))}
                  </select>
                  {validationErrors.adapter && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.adapter}
                    </p>
                  )}
                </div>

                {/* Base URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.config.baseUrl}
                    onChange={(e) =>
                      updateField("config.baseUrl", e.target.value)
                    }
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      validationErrors.baseUrl ? "border-red-300" : ""
                    }`}
                    placeholder="https://api.example.com"
                  />
                  {validationErrors.baseUrl && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.baseUrl}
                    </p>
                  )}
                </div>

                {/* Authentication */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Authentication
                  </h4>

                  {/* Auth Type */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      value={formData.config.auth?.type || "none"}
                      onChange={(e) =>
                        updateField("config.auth.type", e.target.value)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="none">None</option>
                      <option value="apiKey">API Key</option>
                      <option value="basic">Basic Auth</option>
                      <option value="bearer">Bearer Token</option>
                    </select>
                  </div>

                  {/* API Key Fields */}
                  {formData.config.auth?.type === "apiKey" && (
                    <>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Key Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.config.auth.keyName || ""}
                          onChange={(e) =>
                            updateField("config.auth.keyName", e.target.value)
                          }
                          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                            validationErrors.authKeyName ? "border-red-300" : ""
                          }`}
                          placeholder="e.g., X-API-Key"
                        />
                        {validationErrors.authKeyName && (
                          <p className="mt-1 text-sm text-red-600">
                            {validationErrors.authKeyName}
                          </p>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Key Location <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.config.auth.keyLocation || "header"}
                          onChange={(e) =>
                            updateField(
                              "config.auth.keyLocation",
                              e.target.value,
                            )
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="header">Header</option>
                          <option value="query">Query Parameter</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* Credentials (for all auth types except none) */}
                  {formData.config.auth?.type !== "none" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Credentials <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.config.auth?.credentials || ""}
                        onChange={(e) =>
                          updateField("config.auth.credentials", e.target.value)
                        }
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                          validationErrors.authCredentials
                            ? "border-red-300"
                            : ""
                        }`}
                        placeholder="Use ${ENV_VAR} for environment variables"
                      />
                      {validationErrors.authCredentials && (
                        <p className="mt-1 text-sm text-red-600">
                          {validationErrors.authCredentials}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Use ${"{"}ENV_VAR{"}"} to reference environment
                        variables (e.g., ${"{"}API_KEY{"}"})
                      </p>
                    </div>
                  )}
                </div>

                {/* Verifica connessione (whoami / W1) — solo per archives-semantic */}
                {isArchivesSemantic && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">
                        Verifica connessione
                      </h4>
                      <button
                        type="button"
                        onClick={handleTestWhoAmI}
                        disabled={
                          whoamiTest.status === "loading" ||
                          !formData.config.baseUrl.trim() ||
                          (formData.config.auth?.type !== "none" &&
                            !formData.config.auth?.credentials?.trim())
                        }
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {whoamiTest.status === "loading" ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-700 mr-2"></div>
                            Verifico…
                          </>
                        ) : (
                          <>Verifica connessione</>
                        )}
                      </button>
                    </div>

                    <p className="mt-1 text-xs text-gray-500">
                      Interroga <code>/whoami</code> del provider ArcHives e
                      mostra le info del tenant risolto dalla chiave.
                    </p>

                    {whoamiTest.status === "ok" && (
                      <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm">
                        <div className="font-medium text-green-900">
                          ✅ Connesso al portale{" "}
                          <strong>{whoamiTest.whoami.tenant.name}</strong>
                          {whoamiTest.whoami.tenant.id && (
                            <span className="ml-1 font-mono text-xs text-green-700">
                              ({whoamiTest.whoami.tenant.id})
                            </span>
                          )}
                        </div>
                        {whoamiTest.whoami.tenant.description && (
                          <div className="mt-1 text-green-800">
                            {whoamiTest.whoami.tenant.description}
                          </div>
                        )}
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-green-800">
                          {whoamiTest.whoami.counts?.total != null && (
                            <div>
                              <span className="font-medium">
                                Entità totali:
                              </span>{" "}
                              {whoamiTest.whoami.counts.total.toLocaleString(
                                "it-IT",
                              )}
                            </div>
                          )}
                          {whoamiTest.whoami.tenant.language && (
                            <div>
                              <span className="font-medium">Lingua:</span>{" "}
                              {whoamiTest.whoami.tenant.language}
                            </div>
                          )}
                          {whoamiTest.whoami.key?.label && (
                            <div>
                              <span className="font-medium">Chiave:</span>{" "}
                              {whoamiTest.whoami.key.label}
                              {whoamiTest.whoami.key.hint && (
                                <span className="ml-1 font-mono">
                                  {whoamiTest.whoami.key.hint}
                                </span>
                              )}
                            </div>
                          )}
                          {whoamiTest.whoami.key?.expiresAt && (
                            <div>
                              <span className="font-medium">Scade:</span>{" "}
                              {new Date(
                                whoamiTest.whoami.key.expiresAt,
                              ).toLocaleDateString("it-IT")}
                            </div>
                          )}
                          {whoamiTest.whoami.rateLimit && (
                            <div className="col-span-2">
                              <span className="font-medium">Rate limit:</span>{" "}
                              {whoamiTest.whoami.rateLimit.remaining}/
                              {whoamiTest.whoami.rateLimit.limit} nella finestra
                              di {whoamiTest.whoami.rateLimit.windowSeconds}s
                            </div>
                          )}
                          {whoamiTest.whoami.counts?.byEntityType && (
                            <div className="col-span-2 mt-1">
                              <span className="font-medium">Per tipo:</span>{" "}
                              {Object.entries(
                                whoamiTest.whoami.counts.byEntityType,
                              )
                                .map(
                                  ([k, v]) =>
                                    `${k}: ${v.toLocaleString("it-IT")}`,
                                )
                                .join(" · ")}
                            </div>
                          )}
                          <div className="col-span-2 text-green-600">
                            Latenza: {whoamiTest.latency}ms
                          </div>
                        </div>
                      </div>
                    )}

                    {whoamiTest.status === "error" && (
                      <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                        ❌ {whoamiTest.message}
                      </div>
                    )}
                  </div>
                )}

                {/* Webhook di invalidazione cache (E6) — solo per archives-semantic */}
                {isArchivesSemantic && (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Webhook di invalidazione
                    </h4>
                    <label
                      htmlFor="datasource-webhook-secret"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Segreto webhook (variabile d&apos;ambiente)
                    </label>
                    <input
                      id="datasource-webhook-secret"
                      type="text"
                      value={getWebhookSecret(formData.config.options)}
                      onChange={(e) => handleWebhookSecretChange(e.target.value)}
                      aria-invalid={!!validationErrors.webhookSecret}
                      aria-describedby="datasource-webhook-secret-help"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        validationErrors.webhookSecret ? "border-red-300" : ""
                      }`}
                      placeholder="${ARCHIVES_MEIS_WEBHOOK_SECRET}"
                    />
                    {validationErrors.webhookSecret && (
                      <p className="mt-1 text-sm text-red-600">
                        {validationErrors.webhookSecret}
                      </p>
                    )}
                    <p
                      id="datasource-webhook-secret-help"
                      className="mt-1 text-xs text-gray-500"
                    >
                      Opzionale. Il segreto lo genera ArcHives: qui va solo il
                      nome della variabile d&apos;ambiente del site-backend che
                      lo contiene, non il segreto stesso.
                    </p>
                  </div>
                )}

                {/* Enabled Operations */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Enabled Operations
                  </h4>
                  <div className="space-y-2">
                    {availableOperations.map(
                      (op) => (
                        <label key={op} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={
                              formData.enabledOperations?.includes(op as any) ||
                              false
                            }
                            onChange={() => toggleOperation(op)}
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 capitalize">
                            {op}
                          </span>
                        </label>
                      ),
                    )}
                  </div>
                </div>

                {/* Advanced Settings */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Advanced Settings
                  </h4>

                  {/* Timeout */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Timeout (ms)
                    </label>
                    <input
                      type="number"
                      value={formData.config.timeout || 10000}
                      onChange={(e) =>
                        updateField("config.timeout", Number(e.target.value))
                      }
                      min="100"
                      max="60000"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Request timeout in milliseconds (100 - 60000)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>{isEditMode ? "Update" : "Create"}</>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
